import React, { Component } from 'react'
import * as R from 'ramda'
import i18n from 'i18next'
import { observer } from 'mobx-react'
import Select from '../form/Select'
import Input from '../form/Input'
import ChartMath from './ChartMath/ChartMath'
import Checkbox from '../form/Checkbox'
import Button from '../../UniformComponents/Button'
import RangedTextControl from './RangedTextControl'

@observer
class ChartOptions extends Component {
    questionList = []

    constructor(props) {
        super(props)
        this.setQuestionList()
    }

    setQuestionList = () => {
        const {
            pages,
            getQuestionTitle,
            getOptionTitle,
            chart,
        } = this.props

        if (chart.kpiType !== 'EV') return

        this.questionList = pages.reduce((questionList, { questions }) => {
            if (questions) {
                questionList.push(...questions.reduce((options, { question }) => {
                    const { questionTypeCode } = question
                    const hasNumericCodes = question.optionSet
                        ? R.pathOr(false, ['optionSet', 'hasNumericCodes'], question)
                        : R.pathOr(false, ['optionSetX', 'hasNumericCodes'], question)

                    if (!hasNumericCodes || !['RD', 'TB'].includes(questionTypeCode)) return options

                    if (questionTypeCode === 'RD') {
                        options.push({
                            label: `(${questionTypeCode}) ${getQuestionTitle({ question })}`,
                            value: question.id,
                            questionId: question.id,
                        })
                    } else if (questionTypeCode === 'TB') {
                        const titles = question.optionSetY.options.map((option) => ({
                            label: `(${questionTypeCode}) ${getQuestionTitle({ question })} - ${getOptionTitle(option, { question })}`,
                            value: `${question.id}|${option.id}`,
                            set1: option.id,
                            questionId: question.id,
                        }))
                        options.push(...titles)
                    }

                    return options
                }, []))
            }

            return questionList
        }, [])
    }

    typeChange = (type) => {
        const { chart } = this.props
        chart.changeChartType(type.value)
    }

    chartTitleChange = (field) => {
        const { chart, store } = this.props
        const { surveyLocale } = store
        chart.changeTitle({ ...chart.title, [surveyLocale]: field.value })
    }

    getChartTitle = () => {
        const { chart, store } = this.props
        const { survey, surveyLocale } = store
        const { defaultLocale } = survey.metadata
        return R.hasPath([surveyLocale], chart.title)
            ? chart.title[surveyLocale]
            : chart.title[defaultLocale]
    }

    getDisplayBox = () => {
        const { chart, store } = this.props
        const { survey, surveyLocale } = store
        const { defaultLocale } = survey.metadata
        return R.hasPath([surveyLocale], chart.displayText)
            ? chart.displayText[surveyLocale]
            : R.pathOr('', ['displayText', defaultLocale], chart)
    }

    chartDisplayTextChange = (e) => {
        const { target } = e
        const { value } = target
        const { chart, store } = this.props
        const { surveyLocale } = store
        chart.changeDisplayText({ ...chart.displayText, [surveyLocale]: value })
    }

    chartIconChange = (field) => {
        const { chart } = this.props
        chart.changeIcon(field.value)
    }

    changeChartQuestion = ({ value, questionId, set1 }, index) => {
        const { chart } = this.props
        Object.assign(chart.questions[index], {
            id: value,
            questionId,
            set1,
        })
    }

    changeChartLabel = ({ value }, index) => {
        const { chart } = this.props
        Object.assign(chart.questions[index], {
            label: value,
        })
    }

    renderQuestionSelection = () => {
        const { chart } = this.props
        return (
            <div style={{
                padding: '10px 0px', display: 'flex', flexDirection: 'column', gap: '10px',
            }}
            >
                {chart.questions.map((question, index) => (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                                flex: 1, gap: '5px', display: 'flex', flexDirection: 'column',
                            }}
                            >
                                <Select
                                    key={question.uuid}
                                    options={this.questionList}
                                    defaultValue={question.id}
                                    onBlur={(field) => this.changeChartQuestion(field, index)}
                                />
                                <Input
                                    defaultValue={question.label}
                                    placeholder="Optional label"
                                    onBlur={(field) => this.changeChartLabel(field, index)}
                                />
                            </div>
                            <i className="fal fa-trash-alt" onClick={() => chart.removeQuestion(index)} />
                        </div>
                    </div>
                ))}

                <Button
                    label="Add Question"
                    onClick={chart.addQuestion}
                />
            </div>
        )
    }

    render() {
        const {
            chart,
            removeChart,
            chartTypes,
            questionTitle,
            getOptionTitle,
            questionOption,
            question,
            errorChart,
            store,
            pages,
            getQuestionTitle,
        } = this.props

        const questionShortName = questionOption
            ? `${questionTitle} - ${getOptionTitle(questionOption, question)}`
            : questionTitle

        return (
            <div className="option">
                {errorChart ? <div className="errorOverlay" /> : null}
                <div>
                    <div className="chartTitle">
                        <span>{i18n.t('chartCreation.chartTitle')}</span>
                        <div>
                            <Input
                                name={`${chart.chartId}_title`}
                                defaultValue={this.getChartTitle()}
                                onBlur={this.chartTitleChange}
                            />
                        </div>
                        <i className="fal fa-times-circle" onClick={() => removeChart(chart.chartId)} />
                    </div>
                    <Select
                        options={chartTypes}
                        defaultValue={errorChart ? chartTypes[0].value : chart.chartType}
                        onBlur={this.typeChange}
                    />
                    {chart.chartType === 'starChart'
                        ? (
                            <div className="iconField">
                                <span>{i18n.t('chartCreation.icon')}</span>
                                <Input
                                    name={`${chart.chartId}_icon`}
                                    defaultValue={chart.icon}
                                    onBlur={this.chartIconChange}
                                />
                            </div>
                        )
                        : null}
                    {chart.chartType === 'radarChart' && (this.renderQuestionSelection())}
                    <div className="questionName" style={{ display: 'flex' }}>
                        <i className="far fa-hand-point-right" />
                        <span>{questionShortName}</span>
                    </div>
                    {chart.chartType === 'percentageBarChartHorizontal'
                        ? (
                            <ChartMath
                                chart={chart}
                                question={question}
                                store={store}
                                pages={pages}
                                questionTitle={questionTitle}
                                getOptionTitle={getOptionTitle}
                                getQuestionTitle={getQuestionTitle}
                            />
                        )
                        : null}
                    {chart.chartType === 'rangedText'
                        ? (
                            <div className="RangedTextControl">
                                <RangedTextControl
                                    chart={chart}
                                    store={store}
                                />
                            </div>
                        )
                        : null}
                    {chart.chartType !== 'rangedText'
                        ? (
                            <div style={{ display: 'flex', marginTop: '8px' }}>
                                <span>{i18n.t('chartCreation.showDisplayText', 'Show display text box:')}</span>
                                <Checkbox
                                    defaultChecked={chart.showDisplayText}
                                    onBlur={() => { chart.changeShowDisplayText() }}
                                    name={`${chart.chartId}_showDisplayText`}
                                    style={{ marginLeft: '10px' }}
                                />
                            </div>
                        ) : null }
                    {chart.showDisplayText
                        ? (
                            <div>
                                <span>{i18n.t('chartCreation.displayText', 'display text box:')}</span>
                                <div style={{ marginTop: '5px' }}>
                                    <textarea
                                        name={`${chart.chartId}_displayText`}
                                        defaultValue={this.getDisplayBox()}
                                        onBlur={this.chartDisplayTextChange}
                                        style={{ minHeight: '100px' }}
                                    />
                                </div>
                            </div>
                        ) : null}
                </div>
            </div>
        )
    }
}

export default ChartOptions
